import { httpClient } from 'common-utils';
import isFuture from 'date-fns/isFuture';
import {
  BackEndUIText,
  DescriptionLinks,
  DocumentId,
  getUIText,
} from 'preflight-core';
import { omit } from 'remeda';
import { z } from 'zod';

import { JournalSlugs } from './journal-slugs';

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function getJournal({
  publisherSlug,
  journalSlug,
  currency,
  articleTypeId,
  documentId,
}: JournalSlugs): Promise<Journal> {
  const params: string[] = [];

  if (currency) {
    params.push(`currencyCode=${currency}`);
  }

  if (articleTypeId) {
    params.push(`articleTypeId=${Number(articleTypeId)}`);
  }

  if (documentId) {
    params.push(`documentId=${documentId}`);
  }

  const queryString = params.length > 0 ? `?${params.join('&')}` : '';

  const fetchJournal = async (retryCount = 2): Promise<Journal> => {
    try {
      const response = await httpClient.get(
        `${process.env.NEXT_PUBLIC_API_BASE_URL}/publisher/${publisherSlug}/journal/${journalSlug}${queryString}`,
      );
      return Journal.parse(response);
    } catch (error) {
      if (retryCount > 0) {
        await delay(1500); // Wait for 1.5 second before retrying
        return fetchJournal(retryCount - 1); // Retry on failure
      }
      throw error; // Throw error if retries are exhausted
    }
  };

  return fetchJournal();
}

export function getJournalV2({
  publisherSlug,
  journalSlug,
  currency,
  articleTypeId,
  documentId,
}: JournalSlugs): Promise<Journal> {
  const params: string[] = [];

  if (currency) {
    params.push(`currencyCode=${currency}`);
  }

  if (articleTypeId) {
    params.push(`articleTypeId=${Number(articleTypeId)}`);
  }

  if (documentId) {
    params.push(`documentId=${documentId}`);
  }

  const queryString = params.length > 0 ? `?${params.join('&')}` : '';

  const fetchJournal = async (retryCount = 2): Promise<Journal> => {
    try {
      const response = await httpClient.get(
        `${process.env.NEXT_PUBLIC_API_BASE_URL}/v2/publisher/${publisherSlug}/journal/${journalSlug}${queryString}`,
      );
      return Journal.parse(response);
    } catch (error) {
      if (retryCount > 0) {
        await delay(1500);
        return fetchJournal(retryCount - 1);
      }
      throw error;
    }
  };

  return fetchJournal();
}

export function getJournalV3({
  publisherSlug,
  journalSlug,
  currency,
  articleTypeId,
  articleTypeStageId,
  documentId,
}: JournalSlugs & { articleTypeStageId?: number }): Promise<Journal> {
  const params: string[] = [];

  if (currency) {
    params.push(`currencyCode=${currency}`);
  }

  if (articleTypeId) {
    params.push(`articleTypeId=${Number(articleTypeId)}`);
  }

  if (typeof articleTypeStageId !== 'undefined') {
    params.push(`articleTypeStageId=${Number(articleTypeStageId)}`);
  }

  if (documentId) {
    params.push(`documentId=${documentId}`);
  }

  const queryString = params.length > 0 ? `?${params.join('&')}` : '';

  const fetchJournal = async (retryCount = 2): Promise<Journal> => {
    try {
      const response = await httpClient.get(
        `${process.env.NEXT_PUBLIC_API_BASE_URL}/v3/publisher/${publisherSlug}/journal/${journalSlug}${queryString}`,
      );
      return Journal.parse(response);
    } catch (error) {
      if (retryCount > 0) {
        await delay(1500);
        return fetchJournal(retryCount - 1);
      }
      throw error;
    }
  };

  return fetchJournal();
}

const RawCheck = z.object({
  checkKey: z.string(),
  checkIndex: z.number(),
  critical: z.boolean(),
  authorInstructions: z.string(),
  adminDescription: z.string().optional(),
  grouping: z.string(),
  groupingIndex: z.number(),
  groupingLabel: z.string(),
  issues: z.number().optional(),
  category: z.string().optional(),
  categoryLabel: z.string().optional(),
  label: z.string(),
  checksLabel: z.string().optional(),
  groupSubSection: z.string().optional(),
  groupSubSectionLabel: z.string().optional(),
  additionalDescription: z
    .object({
      summary: z.string(),
      additionalDataHeader: z.string().optional(),
      additionalData: z
        .record(z.string(), z.array(z.string()).optional())
        .optional(),
      additionalDataObject: z
        .record(z.string(), z.array(z.unknown()).optional())
        .optional(),
      supplementaryDataHeader: z.string().optional(),
      supplementaryData: z.array(z.string()).optional(),
    })
    .optional(),
});

export type RawCheck = z.infer<typeof RawCheck>;

const Check = RawCheck.merge(
  z.object({
    state: z
      .enum(['idle', 'pending', 'failed', 'successful', 'error'])
      .default('idle'),
  }),
).transform((check) => ({
  ...omit(check, ['authorInstructions']),
  content: {
    text: check.authorInstructions,
    links: [] as DescriptionLinks,
  },
}));

export type Check = Omit<RawCheck, 'authorInstructions'> & {
  state: 'idle' | 'pending' | 'failed' | 'successful' | 'error';
  content: {
    text: string;
    links: DescriptionLinks;
  };
};

export type Journal = z.infer<typeof Journal>;

export type RawJournal = z.infer<typeof RawJournal>;

const RawJournal = z.object({
  checks: z.array(RawCheck),
  demoSubmissionId: DocumentId.optional(),
  gatherAnalytics: z.boolean(),
  label: z.string(),
  logo: z.optional(z.string()),
  publisher: z.object({
    slug: z.string(),
    label: z.string().optional(),
    logo: z.string().optional(),
  }),
  slug: z.string(),
  testModeExpires: z.string().transform((date) => new Date(date)),
  uiDisplayTexts: BackEndUIText,
  url: z.string().optional(),
  salePriceCents: z.number(),
  rapidHumanReviewEnabled: z.boolean().optional(),
  humanReviewEnabled: z.boolean().optional(),
  peerPilotEnabled: z.boolean().optional(),
  availableModules: z.array(z.string()).optional(),
  moduleVoucherAvailability: z
    .array(
      z.object({
        module: z.string(),
        maxNumUses: z.number(),
        remainingUses: z.number(),
      }),
    )
    .optional(),
  localPricing: z
    .object({
      salePriceUsd: z.number().optional(),
      basePrice: z.number().optional(),
      salePrice: z.number().optional(),
      finalPrice: z.number().optional(),
    })
    .optional(),
  articleTypes: z
    .array(
      z.object({
        id: z.number().optional(),
        name: z.string().optional(),
        master: z.boolean().optional(),
        status: z.string().optional(),
        stages: z
          .array(
            z.object({
              id: z.number(),
              name: z.string(),
            }),
          )
          .optional(),
      }),
    )
    .optional(),
  addOnServiceList: z
    .array(
      z.object({
        addOnKey: z.string().optional(),
        basePrice: z.number().optional(),
        salePrice: z.number().optional(),
        title: z.string().optional(),
        addOnServiceId: z.number().optional(),
        gjdJournalName: z.string().optional(),
        gjdPublisherName: z.string().optional(),
        status: z.enum(['PENDING', 'COMPLETED']).optional(),
      }),
    )
    .optional(),
  enabledAddOnServices: z.array(z.string()).optional(),
  currency: z.string().optional(),
});

const Journal = RawJournal.merge(
  z.object({ checks: z.array(Check) }),
).transform((journal) => {
  const sortedChecks = [...journal.checks].sort((checkA, checkB) =>
    checkA.checkIndex === checkB.checkIndex
      ? checkA.label.localeCompare(checkB.label)
      : checkA.checkIndex - checkB.checkIndex,
  );

  let moduleVoucherAvailabilityRecord:
    | Record<string, { maxNumUses: number; remainingUses: number }>
    | null
    | undefined = undefined;
  if (journal.moduleVoucherAvailability) {
    moduleVoucherAvailabilityRecord = Object.fromEntries(
      journal.moduleVoucherAvailability.map((item) => [
        item.module,
        { maxNumUses: item.maxNumUses, remainingUses: item.remainingUses },
      ]),
    );
    if (Object.keys(moduleVoucherAvailabilityRecord).length === 0) {
      moduleVoucherAvailabilityRecord = null;
    }
  } else {
    moduleVoucherAvailabilityRecord = null;
  }

  return {
    ...omit(journal, [
      'checks',
      'testModeExpires',
      'uiDisplayTexts',
      'moduleVoucherAvailability',
    ]),
    checklist: {
      checks: getChecksRecord(sortedChecks),
      groups: getChecklistGroups(sortedChecks),
    },
    testMode: isFuture(journal.testModeExpires),
    uiText: getUIText(
      journal.uiDisplayTexts,
      journal.publisher.slug,
      journal.slug,
    ),
    rapidHumanReviewEnabled:
      journal.rapidHumanReviewEnabled !== undefined &&
      journal.humanReviewEnabled !== undefined
        ? journal.rapidHumanReviewEnabled === false &&
          journal.humanReviewEnabled === false
          ? true
          : journal.rapidHumanReviewEnabled
        : undefined,
    ...(moduleVoucherAvailabilityRecord !== null && {
      moduleVoucherAvailability: moduleVoucherAvailabilityRecord,
    }),
  };
});

export type Checks = Record<Check['checkKey'], Check>;

function getChecksRecord(checks: Check[]): Checks {
  return Object.fromEntries(checks.map((check) => [check.checkKey, check]));
}

type ChecklistGroup = {
  id: Check['grouping'];
  index: number;
  checks: Check['checkKey'][];
  label: Check['groupingLabel'];
};

function getChecklistGroups(checks: Check[]): ChecklistGroup[] {
  const groupRecord = checks.reduce<Record<Check['grouping'], ChecklistGroup>>(
    (groups, check) => {
      const group = groups[check.grouping] ?? {
        id: check.grouping,
        checks: [],
        index: check.groupingIndex,
        label: check.groupingLabel,
      };

      return {
        ...groups,
        [check.grouping]: {
          id: check.grouping,
          checks: [...group.checks, check.checkKey],
          index: check.groupingIndex,
          label: check.groupingLabel,
        },
      };
    },
    {},
  );

  return Object.values(groupRecord).sort((groupA, groupB) =>
    groupA.index === groupB.index
      ? groupA.label.localeCompare(groupB.label)
      : groupA.index - groupB.index,
  );
}

export const JournalNotFoundError = z.object({
  json: z.object({
    errorCode: z.enum(['PUBLISHER_NOT_FOUND', 'NO_ACTIVE_JOURNAL_FOUND']),
  }),
});
